/**
 * Helpers for the Didit identity verification status.
 *
 * The verdict is written to `currentUser.attributes.profile.metadata.diditVerification` by the
 * Didit webhook (see `server/api/didit-webhook.js`). Metadata can only be written through the
 * Integration API, so the client can read it but never set it. What Didit's modal reports to the
 * browser is a UX signal only.
 */

// Statuses Didit reports on a session.
export const DIDIT_STATUS_APPROVED = 'Approved';
export const DIDIT_STATUS_DECLINED = 'Declined';

// The session hasn't reached a verdict yet. A webhook can land with one of these while the user is
// still in the modal, so they don't count as the "updated" metadata after the modal completes.
const TRANSIENT_STATUSES = ['Not Started', 'In Progress', 'Awaiting User'];
const IN_REVIEW_STATUSES = ['In Review', 'Resubmitted'];

export const VERIFICATION_STATE_VERIFIED = 'verified';
export const VERIFICATION_STATE_IN_REVIEW = 'inReview';
export const VERIFICATION_STATE_DECLINED = 'declined';
export const VERIFICATION_STATE_UNVERIFIED = 'unverified';

// After the Didit modal completes, the webhook needs a moment to reach the server.
export const POLL_ATTEMPTS = 3;
export const POLL_DELAY_MS = 3000;

/**
 * @param {Object|null} user The current user, or any user entity (e.g. a listing's author)
 * @returns {{status: string, sessionId?: string, updatedAt?: string, verifiedAt?: string}|null}
 */
export const getDiditVerification = user =>
  user?.attributes?.profile?.metadata?.diditVerification || null;

/**
 * The UI state a Didit status maps to. Anything that isn't a verdict yet (not started, abandoned,
 * expired, ...) is `unverified`: the user gets the call to action again.
 */
export const getVerificationState = verification => {
  const status = verification?.status;
  if (status === DIDIT_STATUS_APPROVED) {
    return VERIFICATION_STATE_VERIFIED;
  }
  if (IN_REVIEW_STATUSES.includes(status)) {
    return VERIFICATION_STATE_IN_REVIEW;
  }
  if (status === DIDIT_STATUS_DECLINED) {
    return VERIFICATION_STATE_DECLINED;
  }
  return VERIFICATION_STATE_UNVERIFIED;
};

/**
 * Has Didit approved this user's identity? Only the metadata counts. It can't be edited by the user,
 * unlike public data, where anyone could set a "verified" flag on their own profile.
 *
 * @param {Object|null} user The current user, or any user entity (e.g. a listing's author)
 * @returns {boolean}
 */
export const isIdentityVerified = user =>
  getVerificationState(getDiditVerification(user)) === VERIFICATION_STATE_VERIFIED;

const defaultWait = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Fetches the current user until the metadata shows a verdict for the given session.
 *
 * Waits `delayMs` before each of `attempts` fetches, giving the webhook time to land. A failed
 * fetch uses up its attempt and the next one may succeed.
 *
 * @param {Object} params
 * @param {Function} params.fetchUser Returns a promise of the freshly fetched current user
 * @param {string} params.sessionId The session that was just completed
 * @param {number} [params.attempts]
 * @param {number} [params.delayMs]
 * @param {Function} [params.wait] Overridable for tests
 * @returns {Promise<Object|null>} The updated diditVerification metadata, or null if it never showed up
 */
export const waitForVerificationUpdate = async ({
  fetchUser,
  sessionId,
  attempts = POLL_ATTEMPTS,
  delayMs = POLL_DELAY_MS,
  wait = defaultWait,
}) => {
  for (let attempt = 0; attempt < attempts; attempt++) {
    await wait(delayMs);

    try {
      const verification = getDiditVerification(await fetchUser());
      const isOurSession = verification?.sessionId === sessionId;
      if (isOurSession && !TRANSIENT_STATUSES.includes(verification.status)) {
        return verification;
      }
    } catch (e) {
      // Try again on the next attempt.
    }
  }

  return null;
};
